import { useEffect } from "react";
import { Clock3, ShoppingBag, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../Redux/hooks";
import { getKeepShopping } from "../../../Redux/ActivityApi/slice";
import "./BrowsingHistoryTimeline.css";

type Props = {
    onClose?: () => void;
};

const formatHistoryDate = (dateValue?: string | Date): string => {
    if (!dateValue) return "Recently";
    try {
        const date = new Date(dateValue);
        if (isNaN(date.getTime())) return "Recently";

        const now = new Date();
        const diffMs = now.getTime() - date.getTime();
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return "Today";
        if (diffDays === 1) return "Yesterday";
        if (diffDays < 7 && diffDays > 1) return `${diffDays} days ago`;

        return date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return "Recently";
    }
};

const BrowsingHistoryTimeline = ({ onClose }: Props) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { keepShopping, loading, keepShoppingLoaded } = useAppSelector(
        (state) => state.activity
    );

    useEffect(() => {
        dispatch(getKeepShopping(10));
    }, [dispatch]);

    const handleProductClick = (productId: string) => {
        onClose?.();
        navigate(`/DetailPage/${productId}`);
    };

    const hasItems = Array.isArray(keepShopping) && keepShopping.length > 0;
    const isLoading = loading && !keepShoppingLoaded && !hasItems;

    return (
        <section className="history-timeline">
            <div className="history-timeline-container">
                <div className="history-timeline-header">
                    <div className="history-timeline-heading">
                        <span className="history-timeline-icon">
                            <Clock3 size={16} strokeWidth={2} />
                        </span>
                        <div>
                            <span className="history-timeline-eyebrow">RECENTLY VIEWED</span>
                            <h2>Your Browsing Journey</h2>
                            <p>Pick up from products and categories you recently explored.</p>
                        </div>
                    </div>
                    <div className="history-timeline-actions">
                        {onClose && (
                            <button
                                type="button"
                                className="history-close"
                                onClick={onClose}
                                aria-label="Close browsing history"
                            >
                                <X size={17} strokeWidth={1.9} />
                            </button>
                        )}
                    </div>
                </div>

                {isLoading ? (
                    <div className="history-scroll">
                        <div className="history-items">
                            {Array.from({ length: 7 }).map((_, index) => (
                                <article
                                    className={`history-item history-skeleton-item ${
                                        index === 0 ? "latest" : ""
                                    }`}
                                    key={index}
                                >
                                    <div className="history-product">
                                        <div className="history-product-image history-skeleton-box" />
                                        <div className="history-product-copy">
                                            <div className="history-skeleton-line history-skeleton-title" />
                                            <div className="history-skeleton-line history-skeleton-desc" />
                                        </div>
                                    </div>
                                    <div className="history-track">
                                        <span className="history-line" />
                                        <span className="history-dot" />
                                        <span className="history-line" />
                                    </div>
                                    <span className="history-date history-skeleton-line history-skeleton-date" />
                                </article>
                            ))}
                        </div>
                    </div>
                ) : !hasItems ? (
                    <div className="history-empty-state">
                        <div className="history-empty-icon">
                            <ShoppingBag size={24} strokeWidth={1.9} />
                        </div>
                        <h3>No browsing history yet</h3>
                        <p>
                            Items and categories you explore while shopping will appear here along your browsing journey.
                        </p>
                    </div>
                ) : (
                    <div className="history-scroll">
                        <div className="history-items">
                            {keepShopping.map((item, index) => {
                                const subtitle =
                                    item.brand?.name ||
                                    (item.categoryHierarchy &&
                                        typeof item.categoryHierarchy === "object" &&
                                        Object.values(item.categoryHierarchy)[0]) ||
                                    "Viewed item";

                                return (
                                    <article
                                        className={`history-item ${index === 0 ? "latest" : ""}`}
                                        key={item._id || index}
                                    >
                                        <button
                                            type="button"
                                            className="history-product"
                                            onClick={() => handleProductClick(item._id)}
                                        >
                                            <div className="history-product-image">
                                                <img
                                                    src={item.mainImage || "/assets/img/salarlogo.png"}
                                                    alt={item.name}
                                                    loading="lazy"
                                                    onError={(e) => {
                                                        e.currentTarget.src = "/assets/img/salarlogo.png";
                                                    }}
                                                />
                                            </div>
                                            <div className="history-product-copy">
                                                <h3 title={item.name}>{item.name}</h3>
                                                <p>{subtitle}</p>
                                            </div>
                                        </button>
                                        <div className="history-track">
                                            <span className="history-line" />
                                            <span className="history-dot" />
                                            <span className="history-line" />
                                        </div>
                                        <span className="history-date">
                                            {formatHistoryDate(item.interactedAt)}
                                        </span>
                                    </article>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default BrowsingHistoryTimeline;